import { autoinject, transient } from "aurelia-framework";
import { Guid } from "../../utilities/guid";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { SaveRequestForQuoteMetaResponse, ValidateQuoteForInputResponse } from "../../models/rfq/rfq.models";
import { GetOrderResponse, SaveOrderMetaResponse, PurchaseProductResponse, RightStoreCheckoutProductInformationModel, RightStoreCheckoutPurchaseModel, GetOrderUrlResponse, ProductCodes, GetEntityOrderResponse } from "../../models/rightstore/rightstore.models";
import { UserClaimsService } from "../authentication/user-claims.service";
import { RightShipDialogServiceConstants } from "../../constants/basics/rightship-dialog.constants";
import { Claims } from "../../constants/authentication/claims";
import { DialogClientService as CommonDialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClientService } from "../authentication/user-client.service";
import { BudgetClientService } from "../budget/budget-client-service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { CustomerSubscriptionModel, OrderLineStatusEnum, SubscriptionTypeEnum } from "@rightship-tech/common-ui/models/rightstore/rightstore.models";
import { RightStoreService as CommonRightStoreService } from "@rightship-tech/common-ui/services/rightstore/rightstore.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { GetCustomerUnSubscriptionResponse } from "../../models/customers/customer-subscription.models";

@autoinject()
@transient()
export class RightStoreService {

    private username: string;
    private rightStoreBaseApiUrl: string

    constructor(private requestClient: RequestClientService, private userClaimsService: UserClaimsService, private dialogClientService: DialogClientService, private commonDialogClientService :CommonDialogClientService,
        private userClientService: UserClientService, private budgetClientService: BudgetClientService, private customerService: CommonCustomerService, private commonRightStoreService: CommonRightStoreService, private configService: ConfigService) {
            this.username = this.userClaimsService.hasPermission(Claims.RightShipUser) ? "" : this.userClientService.currentUser.email;
            this.rightStoreBaseApiUrl = this.configService.config.urls.rightStoreApiUrl;
        }

    public getOrderUrl(orderGuid: Guid): Promise<GetOrderUrlResponse> {
        return this.requestClient.get(`${this.rightStoreBaseApiUrl}/api/order/GetOrderUrl/` + orderGuid, {});
    }

    public getOrder(orderGuid: Guid): Promise<GetOrderResponse> {
        return this.requestClient.get(`${this.rightStoreBaseApiUrl}/api/order/GetOrder/` + orderGuid, {});
    }

    public saveOrderMeta(orderGuid: string, meta: string): Promise<SaveOrderMetaResponse> {
        return this.requestClient.post(`${this.rightStoreBaseApiUrl}/api/order/SaveOrderMeta`, {}, { orderGuid, meta: meta });
    }

    public resetOrderToPendingMeta(orderGuid: Guid): Promise<boolean> {
        return this.requestClient.post(`${this.rightStoreBaseApiUrl}/api/order/ResetOrderToPendingMeta`, {}, { orderGuid, });
    }

    public saveRequestForQuoteMeta(quoteGuid: Guid, meta: string): Promise<SaveRequestForQuoteMetaResponse> {
        return this.requestClient.post(`${this.rightStoreBaseApiUrl}/api/quote/SaveRequestForQuoteMeta`, {}, { quoteGuid, meta: meta });
    }

    public requestProductInformation(customerNumber: number, productNumber: string, userName: string, quantity: number): Promise<RightStoreCheckoutProductInformationModel> {
        return this.requestClient.get(`${this.rightStoreBaseApiUrl}/api/product/RequestProductInformation`, { customerNumber, productNumber, userName, quantity })
    }

    public purchaseProduct(model: RightStoreCheckoutPurchaseModel): Promise<PurchaseProductResponse> {
        return this.requestClient.post(`${this.rightStoreBaseApiUrl}/api/product/PurchaseProduct`, {}, { 
            productNumber: model.productNumber, 
            customerNumber: model.customerNumber, 
            userName: model.userName, 
            quantity: model.checkoutQuantity, 
            paymentMethod: model.paymentMethod, 
            queryParams: model.queryParams,
            priceOverride: model.priceOverride
        });
    }

    public validateQuoteForInput(quoteGuid: string): Promise<ValidateQuoteForInputResponse> {
        return this.requestClient.get(`${this.rightStoreBaseApiUrl}/api/quote/ValidateQuoteForInput`, { quoteId: quoteGuid });
    }

    public getEntityOrderInformation(entityId: number, entityTypeId: number): Promise<GetEntityOrderResponse> {
        return this.requestClient.get(`${this.configService.config.urls.rightStoreApiUrl}/api/order/GetEntityOrder/${entityId}/${entityTypeId}`, {});
    }

    public async submitCloseOut(productNumber: string, standardProductNumber: string, entityId: number, entityType: number, vesselId: number, warningMessage?: string, subtaskAdded?: (() => void)) {
        const canOverridePriceInAppPurchase = this.userClaimsService.hasPermission(Claims.CanOverridePriceInAppPurchase);

        if (this.userClaimsService.hasPermission(Claims.RightShipUser) && 
            (this.userClaimsService.hasPermission(Claims.CanSubmitIncidentCloseout) || this.userClaimsService.hasPermission(Claims.CanSubmitPscCloseout))) {
            this.navigateToSubmitCloseOutInformation("", false, entityId, entityType, warningMessage, subtaskAdded);
            return;
        }
        require("@rightship-tech/common-ui/dialogs/order-summary/dialog-order-summary");
        const businessUnitId = await this.customerService.getBusinessUnitId();
        const env = this.configService?.config?.environment || "development";
        const queryParams = env.toLowerCase() == "production" ? "" : `env=${env}&`;

        const entityOrderInformation = await this.getEntityOrderInformation(entityId, entityType);
        if (entityOrderInformation.orderGuid) {
            this.navigateToSubmitCloseOutInformation(entityOrderInformation.orderGuid, entityOrderInformation.priority, entityId, entityType, warningMessage, subtaskAdded);
            return;
        }

        const customerSubscriptionResp = await this.getCustomerSubscription();
        const customerSubscription = customerSubscriptionResp.customerSubscription;
        const shouldSubmitStandardCloseOut = await this.hasVesselsInLimitedSubscription(vesselId, customerSubscription) 
            || this.hasFullSubscription(customerSubscription);

        if (shouldSubmitStandardCloseOut) {
            const url = await this.sendRequestToCreateOrder(entityId, entityType, businessUnitId.toString(), this.username, standardProductNumber);
            const orderGuid = this.extractOrderGuidFromUrl(url);
            this.navigateToSubmitCloseOutInformation(orderGuid, false, entityId, entityType, warningMessage, subtaskAdded);
        } else {
            this.commonDialogClientService.show(RightShipDialogServiceConstants.URLs.OrderSummaryDialog, {
                customerNumber: businessUnitId,
                standardDescription: productNumber == ProductCodes.PscCloseOutPriority ? "Standard PSC close out" : "Standard INC close out",
                creditBalance: await this.budgetClientService.getCreditBalance(businessUnitId),
                canOverridePriceInAppPurchase: canOverridePriceInAppPurchase,
                productNumber: productNumber,
                userName: this.username,
                queryParams: `${queryParams}EntityId=${entityId}&EntityTypeId=${entityType}&Priority=1`,
                canCustomerInvoice: true,
                preventRedirect: true,
                afterPurchase: (url, priority) => {
                    const orderGuid = this.extractOrderGuidFromUrl(url);
                    this.navigateToSubmitCloseOutInformation(orderGuid, priority, entityId, entityType, warningMessage, subtaskAdded);
                },
                processStandardProduct: async () => {
                    const userName = this.userClientService.currentUser.email;
                    return await this.sendRequestToCreateOrder(entityId, entityType, businessUnitId.toString(), userName, standardProductNumber);
                }
            }, true, false, false, "right");
        }
    }

    public navigateToSubmitCloseOutInformation(orderGuid: string, priority: boolean, entityId: number, entityType: number, warningMessage: string, subtaskAdded: (() => void)) {
        this.commonDialogClientService.hide();
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SubmitCloseOutInformationDialog, {
            isCloseout: true,
            isPriority: priority,
            orderGuid: orderGuid,
            entityId: entityId,
            entityTypeId: entityType,
            warning: warningMessage,
            isPSC: entityType == EntityTypes.PSC,
            onSubtaskAdded: subtaskAdded
        }, true, false, false, "right");
    }

    async sendRequestToCreateOrder(entityId: number, entityType: number, businessUnitId: string, userName: string, productNumber: string): Promise<string> {
        const createCloseOutOrder = {
            customerNumber: businessUnitId,
            userName: userName,
            paymentMethod: 1,
            orderLines: [
                {
                    productNumber: productNumber,
                    status: OrderLineStatusEnum.Initial,
                    queryParams: `EntityId=${entityId}&EntityTypeId=${entityType}`
                }
            ],

        };
        const response = await this.commonRightStoreService.createOrUpdateOrder(createCloseOutOrder);
        return response.orderLines[0].url;
    }

    async hasVesselsInLimitedSubscription(vesselId: number, subscription: CustomerSubscriptionModel): Promise<boolean> {
        const vesselsInSubscription = await this.commonRightStoreService.getCustomerSubscriptionVessels(subscription?.customerSubscriptionId);
        return vesselsInSubscription.vessels.find(x => x.vesselId == vesselId) != null;
    }

    hasFullSubscription(subscription: CustomerSubscriptionModel): boolean {
        return subscription?.subscriptionTypeId == SubscriptionTypeEnum.FullSubscription;
    }

    getCustomerSubscription() {
        const customerId = this.userClientService.currentUser.settings.customerId ? this.userClientService.currentUser.settings.customerId : 'null';
        return this.commonRightStoreService.getCustomerSubscription(customerId, ProductCodes.InformationServiceSubscription);
    }

    extractOrderGuidFromUrl(url: string): string {
        const param = url.split('?')[0].split('/');
        return param[param.length - 1];
    }

    public getCustomerUnsubscribedSubscriptions(customerId: number): Promise<GetCustomerUnSubscriptionResponse> {
        return this.requestClient.post(`${this.rightStoreBaseApiUrl}/api/CustomerSubscriptionAuth/GetCustomerUnsubscribedSubscriptions`, {}, { customerId: customerId });
    }
}